import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";
import { pFetch, authQuery } from "../portal/portalAuth";
import "./VbVergleichPage.css";

const API = `${API_BASE}/vb-vergleich`;

const CFG_DEFAULT = {
  eingabe: {
    alt_suffix: "_Dopix", neu_suffix: "_MText",
    vnr_muster: "", inhalt_muster: "",
    modus: "ordner",
    jira_testaufstellung: true, nur_aus_testaufstellung: false,
    bug_export: true, vorbewertung: true,
    vnr_whitelist_text: "",
  },
  vergleich: {
    absatz_paarung: "fuzzy", paar_schwelle: 0.5,
    whitespace_eigene_kategorie: true,
    baustein_modus: false, baustein_marker: "",
  },
  // Hotfix 6.13.1: Dokumentenschlüssel „DXX …" unten auf jeder Seite (je Dokument anders) nie vergleichen —
  // nur Zeilen, die mit „DXX " beginnen (auf derselben Höhe stehen in Bausteinlisten echte Inhalte)
  regionen_ignorieren: [
    { name: "Dokumentenschlüssel (DXX …)", seiten: ["alle"], rechteck: [0, 765, 595, 805], gilt: "beide", aktiv: true, muster: "^DXX\\s" },
  ],
  bekannte_fehler: [
    { name: "Umbruch wg. Schriftart (kein Fehler)", regel: "umbruch_artefakt", aktiv: true },
  ],
  buendelung: {
    modus: "pro_fehlerbild", systematisch_ab: 10,
    beispiele_pro_cluster: 3, umbruch_artefakte_ausblenden: false,
  },
  ausgabe: {
    formate: ["excel", "html"],
    titel: "Fehler-Cluster-Report — Dopix vs. M/Text", lauf: "",
  },
};

const deepMerge = (base, over) => {
  if (Array.isArray(base) || Array.isArray(over) || typeof base !== "object" || typeof over !== "object" || !base || !over) {
    return over !== undefined ? over : base;
  }
  const out = { ...base };
  for (const k of Object.keys(over)) out[k] = deepMerge(base[k], over[k]);
  return out;
};

/* Wiederverwendbare Drag&Drop-Zone (Muster ToolsKomprimierungPage) */
function DropZone({ icon, label, hint, onFiles, accept, compact }) {
  const inputRef = useRef(null);
  const dropRef = useRef(null);
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onOver = (e) => { e.preventDefault(); el.classList.add("dragover"); };
    const onLeave = () => el.classList.remove("dragover");
    const onDrop = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
      onFiles(e.dataTransfer.files);
    };
    el.addEventListener("dragover", onOver);
    el.addEventListener("dragleave", onLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onOver);
      el.removeEventListener("dragleave", onLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [onFiles]);
  return (
    <div ref={dropRef} className={`vb-drop${compact ? " compact" : ""}`} onClick={() => inputRef.current?.click()}>
      <div className="vb-drop-icon">{icon}</div>
      <div className="vb-drop-text">{label}</div>
      {hint && <div className="vb-drop-hint">{hint}</div>}
      <input ref={inputRef} type="file" multiple accept={accept} style={{ display: "none" }}
             onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
    </div>
  );
}

export default function VbVergleichPage({ portal = false, onBack = null, backLabel = null }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("neu");

  // ── Neuer Lauf ──
  const [cfg, setCfg] = useState(CFG_DEFAULT);
  const [profile, setProfile] = useState([]);
  const [selProfil, setSelProfil] = useState("");
  const [laufId, setLaufId] = useState(null);
  const [dateien, setDateien] = useState({ alt: [], alt_mtext: [], neu: [], extras: [] });
  const [uploadInfo, setUploadInfo] = useState(null); // {busy, done, total, unzuordenbar:[]}
  const [lauf, setLauf] = useState(null);             // Polling-Status
  const [startBusy, setStartBusy] = useState(false);
  const [startModus, setStartModus] = useState("");
  const [insProfil, setInsProfil] = useState(null);   // Welle 108: {busy} | {ok,…} | {fehler}
  const [fehler, setFehler] = useState("");
  const pollRef = useRef(null);

  // ── Historie / Profile ──
  const [laeufe, setLaeufe] = useState([]);
  const [profilDetail, setProfilDetail] = useState(null);

  // ── Ergebnis: Dokumenten-Tabelle ──
  const [docFilter, setDocFilter] = useState("");
  const [docNurFehler, setDocNurFehler] = useState(true);

  const setC = (pfad, wert) => {
    setCfg(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const teile = pfad.split(".");
      let o = copy;
      for (let i = 0; i < teile.length - 1; i++) o = o[teile[i]];
      o[teile[teile.length - 1]] = wert;
      return copy;
    });
  };

  const loadProfile = useCallback(async () => {
    try {
      const r = await pFetch(`${API}/profile`);
      const d = await r.json();
      setProfile(d.profile || []);
    } catch { /* Backend nicht erreichbar */ }
  }, []);

  const loadLaeufe = useCallback(async () => {
    try {
      const r = await pFetch(`${API}/laeufe`);
      const d = await r.json();
      setLaeufe(d.laeufe || []);
    } catch { /* ignorieren */ }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);
  useEffect(() => { if (tab === "laeufe") loadLaeufe(); }, [tab, loadLaeufe]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const profilAnwenden = (id) => {
    setSelProfil(id);
    const p = profile.find(x => String(x.id) === String(id));
    if (p) setCfg(deepMerge(CFG_DEFAULT, p.config || {}));
    else setCfg(CFG_DEFAULT);
  };

  const profilSpeichern = async () => {
    const name = window.prompt("Profil-Name:", profile.find(x => String(x.id) === String(selProfil))?.name || "");
    if (!name) return;
    const c = JSON.parse(JSON.stringify(cfg));
    delete c.eingabe.vnr_whitelist_text;
    const r = await pFetch(`${API}/profile`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, config: c }),
    });
    const d = await r.json().catch(() => ({}));
    // Das gespeicherte Profil auswählen — sonst schreibt „ins Profil übernehmen"
    // (Welle 108) in das vorher gewählte Profil.
    if (d.id) setSelProfil(String(d.id));
    loadProfile();
  };

  // ── Upload ──
  const ensureLauf = async () => {
    if (laufId) return laufId;
    const r = await pFetch(`${API}/laeufe`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: cfg.ausgabe.lauf || "", profil_id: selProfil ? +selProfil : null }),
    });
    const d = await r.json();
    setLaufId(d.lauf_id);
    return d.lauf_id;
  };

  const ladeDateien = async (id) => {
    const r = await pFetch(`${API}/laeufe/${id}/dateien`);
    const d = await r.json();
    setDateien({ alt: [], alt_mtext: [], neu: [], extras: [], ...d });
  };

  const upload = useCallback(async (fileList, ziel) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setFehler("");
    try {
      const id = await ensureLauf();
      const BATCH = 15;
      let done = 0;
      const unzu = [];
      setUploadInfo({ busy: true, done: 0, total: arr.length, unzuordenbar: [] });
      for (let i = 0; i < arr.length; i += BATCH) {
        const fd = new FormData();
        arr.slice(i, i + BATCH).forEach(f => fd.append("files", f));
        const q = new URLSearchParams({
          ziel,
          alt_suffix: cfg.eingabe.alt_suffix || "_Dopix",
          neu_suffix: cfg.eingabe.neu_suffix || "_MText",
        });
        const r = await pFetch(`${API}/laeufe/${id}/upload?${q}`, { method: "POST", body: fd });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Upload fehlgeschlagen");
        unzu.push(...(d.unzuordenbar || []));
        done = Math.min(arr.length, i + BATCH);
        setUploadInfo({ busy: true, done, total: arr.length, unzuordenbar: unzu });
      }
      setUploadInfo({ busy: false, done, total: arr.length, unzuordenbar: unzu });
      await ladeDateien(id);
    } catch (e) {
      setFehler(String(e.message || e));
      setUploadInfo(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laufId, cfg.eingabe.alt_suffix, cfg.eingabe.neu_suffix, cfg.ausgabe.lauf, selProfil]);

  const dateiLoeschen = async (ziel, name) => {
    if (!laufId) return;
    await pFetch(`${API}/laeufe/${laufId}/dateien?ziel=${ziel}&name=${encodeURIComponent(name)}`, { method: "DELETE" });
    await ladeDateien(laufId);
  };

  // ── Start + Polling ──
  const pollen = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await pFetch(`${API}/laeufe/${id}`);
        const d = await r.json();
        setLauf(d);
        if (d.status !== "running") {
          clearInterval(pollRef.current);
          setStartBusy(false);
        }
      } catch { /* nächster Tick */ }
    }, 1500);
  };

  // vergleichsModus "" = normaler Vergleich (wie bisher), "bausteine_variablen" = Baustein- und
  // Variablenvergleich (Meeting 05.10.2026) — gilt nur für diesen Lauf, das Profil bleibt unverändert
  const starten = async (vergleichsModus = "") => {
    if (!laufId) { setFehler("Bitte zuerst PDFs hochladen."); return; }
    setFehler("");
    setStartBusy(true);
    setStartModus(vergleichsModus);
    setLauf(null);
    const c = JSON.parse(JSON.stringify(cfg));
    if (vergleichsModus) c.vergleich = { ...c.vergleich, modus: vergleichsModus };
    else if (c.vergleich) delete c.vergleich.modus;
    // Extras nur anfordern, wenn passende Datei hochgeladen wurde
    const ext = dateien.extras || [];
    c.eingabe.jira_testaufstellung = c.eingabe.jira_testaufstellung && ext.some(f => f.toLowerCase().endsWith(".docx"));
    c.eingabe.bug_export = c.eingabe.bug_export && ext.some(f => f.toLowerCase().endsWith(".csv"));
    c.eingabe.jira_testfaelle_csv = c.eingabe.jira_testfaelle_csv && ext.some(f => f.toLowerCase().endsWith(".csv"));
    c.eingabe.vorbewertung = c.eingabe.vorbewertung && ext.some(f => f.toLowerCase().endsWith(".xlsx"));
    if (ext.some(f => f.toLowerCase().endsWith(".html")) && c.eingabe.modus === "html_report") {
      // Legacy-Report auswerten statt PDFs
    } else {
      c.eingabe.modus = "ordner";
    }
    try {
      const r = await pFetch(`${API}/laeufe/${laufId}/start`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(c),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Start fehlgeschlagen");
      pollen(laufId);
    } catch (e) {
      setFehler(String(e.message || e));
      setStartBusy(false);
    }
  };

  const neuerLauf = () => {
    clearInterval(pollRef.current);
    setLaufId(null);
    setDateien({ alt: [], alt_mtext: [], neu: [], extras: [] });
    setInsProfil(null);
    setLauf(null);
    setUploadInfo(null);
    setStartBusy(false);
    setFehler("");
  };

  const configUebernehmen = async (id) => {
    const r = await pFetch(`${API}/laeufe/${id}`);
    const d = await r.json();
    if (d.config && Object.keys(d.config).length) {
      const c = deepMerge(CFG_DEFAULT, d.config);
      // Server-Pfade aus der gespeicherten Config entfernen
      delete c.eingabe.ordner; delete c.eingabe.ordner_neu;
      delete c.eingabe.alt_pdf; delete c.eingabe.neu_pdf;
      delete c.eingabe.nur_vnr_aus; delete c.eingabe.html_report;
      c.eingabe.jira_testaufstellung = true; c.eingabe.bug_export = true; c.eingabe.vorbewertung = true;
      delete c.ausgabe.ordner;
      setCfg(c);
    }
    neuerLauf();
    setTab("neu");
  };

  const laufLoeschen = async (id) => {
    if (!window.confirm("Lauf und alle Dateien wirklich löschen?")) return;
    await pFetch(`${API}/laeufe/${id}`, { method: "DELETE" });
    loadLaeufe();
  };

  const profilLoeschen = async (id) => {
    if (!window.confirm("Profil wirklich löschen?")) return;
    await pFetch(`${API}/profile/${id}`, { method: "DELETE" });
    loadProfile();
  };

  const profilUmbenennen = async (p) => {
    const name = window.prompt("Neuer Name:", p.name);
    if (!name || name === p.name) return;
    await pFetch(`${API}/profile/${p.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, config: p.config }),
    });
    loadProfile();
  };

  // ── Regionen / Bekannte Fehler Editor-Helfer ──
  const regionAdd = () => setC("regionen_ignorieren",
    [...cfg.regionen_ignorieren, { name: "Neue Region", seiten: ["alle"], rechteck: [0, 0, 595, 60], gilt: "beide", aktiv: true }]);
  const regionSet = (i, feld, wert) => {
    const arr = JSON.parse(JSON.stringify(cfg.regionen_ignorieren));
    arr[i][feld] = wert;
    setC("regionen_ignorieren", arr);
  };
  const bfAdd = () => setC("bekannte_fehler",
    [...cfg.bekannte_fehler, { name: "Bekannter Fehler", alt: "", neu: "", aktiv: true }]);
  const bfSet = (i, feld, wert) => {
    const arr = JSON.parse(JSON.stringify(cfg.bekannte_fehler));
    arr[i][feld] = wert;
    setC("bekannte_fehler", arr);
  };

  const [rasterImg, setRasterImg] = useState(null);
  const [rasterSeite, setRasterSeite] = useState(1);
  const rasterAnzeigen = async () => {
    const name = dateien.alt[0] || dateien.neu[0];
    if (!laufId || !name) { setFehler("Für das Hilfsbild zuerst eine PDF hochladen."); return; }
    const seite = dateien.alt[0] ? "alt" : "neu";
    const r = await pFetch(`${API}/laeufe/${laufId}/raster?seite=${seite}&name=${encodeURIComponent(name)}&pdf_seite=${rasterSeite}`, { method: "POST" });
    if (r.ok) setRasterImg(URL.createObjectURL(await r.blob()));
  };

  // Welle 108: Genehmigungen aus der hochgeladenen Excel ins Profil schreiben — der
  // nächste Lauf mit diesem Profil braucht die Excel dann nicht mehr.
  const insProfilUebernehmen = async () => {
    let id = selProfil;
    try {
      if (!id) {
        const name = window.prompt("Es ist noch kein Profil gewählt. Unter welchem Namen sollen die "
          + "Einstellungen samt Genehmigungen gespeichert werden?", "");
        if (!name) return;
        const c = JSON.parse(JSON.stringify(cfg));
        delete c.eingabe.vnr_whitelist_text;
        const r0 = await pFetch(`${API}/profile`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, config: c }),
        });
        const d0 = await r0.json();
        if (!r0.ok || !d0.id) throw new Error(d0.detail || "Profil konnte nicht angelegt werden");
        id = String(d0.id);
      }
      setInsProfil({ busy: true });
      const r = await pFetch(`${API}/laeufe/${laufId}/genehmigte-ins-profil`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profil_id: Number(id) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Übernahme fehlgeschlagen");
      // Profil wieder anwenden, damit der nächste Lauf die Regeln mitschickt
      setSelProfil(String(id));
      setCfg(deepMerge(CFG_DEFAULT, d.config || {}));
      loadProfile();
      setInsProfil({ ok: true, ...d });
    } catch (e) {
      setInsProfil({ fehler: String(e.message || e) });
    }
  };

  const statusBadge = (s) => (
    <span className={`vb-status vb-status-${s}`}>
      {{ neu: "Neu", running: "Läuft…", fertig: "Fertig", fehler: "Fehler", abgebrochen: "Abgebrochen" }[s] || s}
    </span>
  );

  const stat = lauf?.statistik || {};
  const fertig = lauf?.status === "fertig";
  // Welle 107: ein Lauf vergleicht entweder Dopix oder MText alt mit MText neu
  const nMtextAlt = (dateien.alt_mtext || []).length;
  const beideAlt = dateien.alt.length > 0 && nMtextAlt > 0;
  const genehmigtAusExcel = (stat.genehmigt_liste || []).filter(g => g.quelle !== "Profil");
  const profilName = profile.find(x => String(x.id) === String(selProfil))?.name || "";
  const vergleichsart = (stat.quelle_alt || (nMtextAlt ? "mtext" : "dopix")) === "mtext"
    ? "MText alt → MText neu" : "Dopix → MText";

  // Dokumenten-Tabelle: filtern + sortieren (meiste echte Fehler zuerst)
  const dokumente = (stat.dokumente || [])
    .filter(d => !docFilter || String(d.vnr).toLowerCase().includes(docFilter.toLowerCase()))
    .filter(d => !docNurFehler || (d.echte || 0) > 0)
    .sort((a, b) => (b.echte || 0) - (a.echte || 0));

  return (
    <div className="vb-page">
      <div className="vb-header">
        {!portal && (
          <button className="vb-back" onClick={onBack || (() => navigate("/"))}>
            {backLabel || "← Home"}
          </button>
        )}
        <h1>PDF Vergleich</h1>
        <span className="vb-sub">Dopix oder MText alt ↔ MText</span>
        <div className="vb-tabs">
          {[["neu", "▶ Neuer Lauf"], ["laeufe", "🗂 Läufe"], ["profile", "⚙ Profile"]].map(([id, label]) => (
            <button key={id} className={`vb-tab${tab === id ? " active" : ""}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
      </div>

      {fehler && <div className="vb-error">{fehler}</div>}

      {/* ════════ Tab: Neuer Lauf ════════ */}
      {tab === "neu" && (
        <>
          {/* Profil */}
          <div className="vb-card">
            <div className="vb-card-title">Profil</div>
            <div className="vb-row-flex">
              <select className="vb-input" value={selProfil} onChange={(e) => profilAnwenden(e.target.value)}>
                <option value="">— ohne Profil (Standardwerte) —</option>
                {profile.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button className="vb-btn" onClick={profilSpeichern}>💾 Einstellungen als Profil speichern</button>
              <input className="vb-input" style={{ maxWidth: 260 }} placeholder="Lauf-Bezeichnung (optional)"
                     value={cfg.ausgabe.lauf} onChange={(e) => setC("ausgabe.lauf", e.target.value)} />
            </div>
          </div>

          {/* Dateien */}
          <div className="vb-card">
            <div className="vb-card-title">Dateien {laufId && <span className="vb-mini">Lauf {laufId.slice(0, 8)}…</span>}
              {(dateien.alt.length + nMtextAlt + dateien.neu.length > 0 || lauf) && (
                <button className="vb-btn small ghost" style={{ marginLeft: "auto" }} onClick={neuerLauf}>✕ Neu beginnen</button>
              )}
            </div>
            {/* ALT-Seite geteilt: Dopix ODER ein älterer MText-Stand (Welle 107) */}
            <div className="vb-drop-grid">
              <div className="vb-drop-halb">
                <DropZone icon="📕" label={`ALT – Dopix (${cfg.eingabe.alt_suffix || "_Dopix"})`}
                          hint="Dopix-PDFs oder ZIP" onFiles={(f) => upload(f, "alt")} accept=".pdf,.zip" />
                <DropZone icon="📘" label="ALT – MText alt"
                          hint="älterer MText-Stand · PDFs oder ZIP" onFiles={(f) => upload(f, "alt_mtext")} accept=".pdf,.zip" />
              </div>
              <DropZone icon="📗" label={`NEU – MText (${cfg.eingabe.neu_suffix || "_MText"})`}
                        hint="PDFs oder ZIP hier ablegen" onFiles={(f) => upload(f, "neu")} accept=".pdf,.zip" />
            </div>
            <div className="vb-drop-grid">
              <DropZone compact icon="🔀" label="Gemischt — automatisch nach Kennung sortieren"
                        hint={`${cfg.eingabe.alt_suffix} → ALT Dopix · ${cfg.eingabe.neu_suffix} → NEU · MText alt bitte ins eigene Feld`}
                        onFiles={(f) => upload(f, "auto")} accept=".pdf,.zip" />
              <DropZone compact icon="📎" label="Extras (optional)"
                        hint="Excel früherer Lauf („genehmigt“ blendet aus) · Jira · Legacy-HTML"
                        onFiles={(f) => upload(f, "extras")} accept=".docx,.csv,.xlsx,.html,.zip" />
            </div>
            {uploadInfo && (
              <div className="vb-upload-status">
                {uploadInfo.busy ? `⏫ Hochladen… ${uploadInfo.done}/${uploadInfo.total}` : `✓ ${uploadInfo.done} Datei(en) übertragen`}
                {uploadInfo.unzuordenbar.length > 0 && (
                  <span className="vb-warn"> · nicht zuordenbar: {uploadInfo.unzuordenbar.join(", ")}</span>
                )}
              </div>
            )}
            {beideAlt && (
              <div className="vb-error">Ein Lauf vergleicht entweder Dopix oder MText alt mit MText neu —
                bitte eines der beiden ALT-Felder leeren.</div>
            )}
            {(dateien.alt.length > 0 || nMtextAlt > 0 || dateien.neu.length > 0 || dateien.extras.length > 0) && (
              <div className="vb-dateien">
                {["alt", "alt_mtext", "neu", "extras"].map(z => (dateien[z] || []).length > 0 && (
                  <div key={z} className="vb-datei-spalte">
                    <div className="vb-datei-kopf">{{ alt: "📕 ALT · Dopix", alt_mtext: "📘 ALT · MText alt", neu: "📗 NEU · MText", extras: "📎 Extras" }[z]} · {dateien[z].length}</div>
                    <div className="vb-datei-liste">
                      {dateien[z].slice(0, 100).map(name => (
                        <div key={name} className="vb-datei">
                          <span title={name}>{name}</span>
                          <button className="vb-x" onClick={() => dateiLoeschen(z, name)}>✕</button>
                        </div>
                      ))}
                      {dateien[z].length > 100 && <div className="vb-mini">… und {dateien[z].length - 100} weitere</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Optionen */}
          <div className="vb-card">
            <div className="vb-card-title">Optionen</div>

            <details className="vb-sect" open>
              <summary>Kennungen & Zuordnung</summary>
              <div className="vb-grid4">
                <label>ALT-Kennung<input className="vb-input" value={cfg.eingabe.alt_suffix} onChange={(e) => setC("eingabe.alt_suffix", e.target.value)} /></label>
                <label>NEU-Kennung<input className="vb-input" value={cfg.eingabe.neu_suffix} onChange={(e) => setC("eingabe.neu_suffix", e.target.value)} /></label>
                <label>VNR-Muster (Regex, optional)<input className="vb-input" placeholder="z. B. ^(\d+)_" value={cfg.eingabe.vnr_muster || ""} onChange={(e) => setC("eingabe.vnr_muster", e.target.value)} /></label>
                <label>Inhalts-Muster (Regex, optional)<input className="vb-input" placeholder="Zuordnung über Seite-1-Inhalt" value={cfg.eingabe.inhalt_muster || ""} onChange={(e) => setC("eingabe.inhalt_muster", e.target.value)} /></label>
              </div>
              <label className="vb-block">Nur diese VNRs vergleichen (eine je Zeile, leer = alle)
                <textarea className="vb-input" rows={3} value={cfg.eingabe.vnr_whitelist_text}
                          onChange={(e) => setC("eingabe.vnr_whitelist_text", e.target.value)} />
              </label>
              {dateien.extras.length > 0 && (
                <div className="vb-checks">
                  {dateien.extras.some(f => f.toLowerCase().endsWith(".docx")) && (
                    <>
                      <label><input type="checkbox" checked={cfg.eingabe.jira_testaufstellung} onChange={(e) => setC("eingabe.jira_testaufstellung", e.target.checked)} /> Jira-Testaufstellung nutzen</label>
                      <label><input type="checkbox" checked={cfg.eingabe.nur_aus_testaufstellung} onChange={(e) => setC("eingabe.nur_aus_testaufstellung", e.target.checked)} /> nur VNRs aus der Testaufstellung</label>
                    </>
                  )}
                  {dateien.extras.some(f => f.toLowerCase().endsWith(".csv")) && (
                    <label><input type="checkbox" checked={cfg.eingabe.bug_export} onChange={(e) => setC("eingabe.bug_export", e.target.checked)} /> Jira-Bug-Export abgleichen</label>
                  )}
                  {dateien.extras.some(f => f.toLowerCase().endsWith(".csv")) && (
                    <label><input type="checkbox" checked={cfg.eingabe.jira_testfaelle_csv} onChange={(e) => setC("eingabe.jira_testfaelle_csv", e.target.checked)} /> Jira-Testfälle aus CSV lesen</label>
                  )}
                  {dateien.extras.some(f => f.toLowerCase().endsWith(".xlsx")) && (
                    <label title="Excel eines früheren Laufs ins Extras-Feld legen: „genehmigt“ wird ausgeblendet, „bug“/„beobachten“ bleiben mit Status sichtbar"><input type="checkbox" checked={cfg.eingabe.vorbewertung} onChange={(e) => setC("eingabe.vorbewertung", e.target.checked)} /> Vorbewertung übernehmen (Excel: genehmigte ausblenden)</label>
                  )}
                  {dateien.extras.some(f => f.toLowerCase().endsWith(".html")) && (
                    <label><input type="checkbox" checked={cfg.eingabe.modus === "html_report"} onChange={(e) => setC("eingabe.modus", e.target.checked ? "html_report" : "ordner")} /> Legacy-HTML-Report auswerten (statt PDFs)</label>
                  )}
                </div>
              )}
            </details>

            <details className="vb-sect">
              <summary>Vergleich</summary>
              <div className="vb-grid4">
                <label>Absatz-Paarung
                  <select className="vb-input" value={cfg.vergleich.absatz_paarung} onChange={(e) => setC("vergleich.absatz_paarung", e.target.value)}>
                    <option value="fuzzy">fuzzy (empfohlen)</option>
                    <option value="index">index (stur nach Reihenfolge)</option>
                  </select>
                </label>
                <label>Paar-Schwelle (0–1)<input className="vb-input" type="number" min="0" max="1" step="0.05" value={cfg.vergleich.paar_schwelle} onChange={(e) => setC("vergleich.paar_schwelle", +e.target.value)} /></label>
                <label className="vb-check-inline"><input type="checkbox" checked={cfg.vergleich.baustein_modus} onChange={(e) => setC("vergleich.baustein_modus", e.target.checked)} /> Baustein-Modus (Testdrucke)</label>
                <label>Baustein-Marker (Regex, optional)<input className="vb-input" disabled={!cfg.vergleich.baustein_modus} value={cfg.vergleich.baustein_marker || ""} onChange={(e) => setC("vergleich.baustein_marker", e.target.value)} /></label>
                <label title="Für „🧩 Bausteine & Variablen vergleichen“: woran eine Variable erkannt wird. Leer = Standard (Großbuchstaben + mind. zwei Ziffern, z. B. VBSA05, VBSA0116, VBSA0102_2A)">
                  Variablen-Muster (Regex, optional, Bausteinvergleich)
                  <input className="vb-input" placeholder="[A-Z][A-Z0-9$]*\d{2,}[A-Z0-9$]*(?:_\d[A-Z])?" value={cfg.vergleich.variablen_muster || ""} onChange={(e) => setC("vergleich.variablen_muster", e.target.value)} /></label>
              </div>
            </details>

            <details className="vb-sect">
              <summary>Regionen ignorieren ({cfg.regionen_ignorieren.filter(r => r.aktiv !== false).length} aktiv)</summary>
              <table className="vb-table">
                <thead><tr><th>Aktiv</th><th>Name</th><th>Seiten</th><th title="PDF-Punkte, Ursprung oben links">Links</th><th>Oben</th><th>Rechts</th><th>Unten</th><th>Höhe</th>
                  <th title="Leer = alles im Bereich ignorieren. Mit Regex: nur ganze Zeilen im Bereich, deren Text passt (z. B. ^DXX\s)">Nur Zeilen mit Text (Regex)</th><th>Gilt</th><th></th></tr></thead>
                <tbody>
                  {cfg.regionen_ignorieren.map((r, i) => (
                    <tr key={i}>
                      <td><input type="checkbox" checked={r.aktiv !== false} onChange={(e) => regionSet(i, "aktiv", e.target.checked)} /></td>
                      <td><input className="vb-input" value={r.name || ""} onChange={(e) => regionSet(i, "name", e.target.value)} /></td>
                      <td><input className="vb-input" style={{ width: 90 }} value={(r.seiten || ["alle"]).join(",")}
                                 onChange={(e) => regionSet(i, "seiten", e.target.value.split(",").map(s => s.trim() === "alle" ? "alle" : +s.trim() || s.trim()))} /></td>
                      {[0, 1, 2, 3].map((k) => (
                        <td key={k}><input className="vb-input" style={{ width: 62 }} type="number" step="1" value={(r.rechteck || [0, 0, 0, 0])[k] ?? 0}
                          onChange={(e) => { const re = [...(r.rechteck || [0, 0, 0, 0])]; re[k] = +e.target.value || 0; regionSet(i, "rechteck", re); }} /></td>
                      ))}
                      <td className="vb-mini" title="Unten − Oben">{Math.round(((r.rechteck || [])[3] || 0) - ((r.rechteck || [])[1] || 0))} pt</td>
                      <td><input className="vb-input" style={{ width: 120 }} placeholder="(alles)" value={r.muster || ""} onChange={(e) => regionSet(i, "muster", e.target.value)} /></td>
                      <td>
                        <select className="vb-input" value={r.gilt || "beide"} onChange={(e) => regionSet(i, "gilt", e.target.value)}>
                          <option value="beide">beide</option><option value="alt">nur ALT</option><option value="neu">nur NEU</option>
                        </select>
                      </td>
                      <td><button className="vb-x" onClick={() => setC("regionen_ignorieren", cfg.regionen_ignorieren.filter((_, j) => j !== i))}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="vb-row-flex">
                <button className="vb-btn small" onClick={regionAdd}>+ Region</button>
                <span className="vb-mini">Koordinaten in PDF-Punkten, Ursprung oben links (A4 ≈ 595×842). Mit „Nur Zeilen mit Text“ fällt im Bereich nur die passende Zeile weg.</span>
                <span style={{ marginLeft: "auto" }} />
                <label className="vb-mini">Seite <input className="vb-input" style={{ width: 60 }} type="number" min="1" value={rasterSeite} onChange={(e) => setRasterSeite(+e.target.value || 1)} /></label>
                <button className="vb-btn small" onClick={rasterAnzeigen}>📐 Koordinaten-Hilfsbild</button>
              </div>
              {rasterImg && (
                <div className="vb-raster"><img src={rasterImg} alt="Koordinaten-Raster" /><button className="vb-btn small ghost" onClick={() => setRasterImg(null)}>Schließen</button></div>
              )}
            </details>

            <details className="vb-sect">
              <summary>Bekannte Fehler ({cfg.bekannte_fehler.filter(b => b.aktiv !== false).length} aktiv
                {cfg.bekannte_fehler.some(b => b.genehmigt && b.aktiv !== false)
                  ? `, davon ${cfg.bekannte_fehler.filter(b => b.genehmigt && b.aktiv !== false).length} genehmigt` : ""})</summary>
              <table className="vb-table">
                <thead><tr><th>Aktiv</th><th>Name</th><th>Typ</th><th>Alt / Regex-Alt</th><th>Neu / Regex-Neu</th>
                  <th title="bekannt = grau, zählt nicht · genehmigt = ganz ausgeblendet (wie mit Excel)">Wirkung</th><th></th></tr></thead>
                <tbody>
                  {cfg.bekannte_fehler.map((b, i) => {
                    const typ = b.regel ? b.regel : (b.regex_alt !== undefined || b.regex_neu !== undefined) ? "regex" : "woertlich";
                    return (
                      <tr key={i}>
                        <td><input type="checkbox" checked={b.aktiv !== false} onChange={(e) => bfSet(i, "aktiv", e.target.checked)} /></td>
                        <td><input className="vb-input" value={b.name || ""} onChange={(e) => bfSet(i, "name", e.target.value)} /></td>
                        <td>
                          <select className="vb-input" value={typ} onChange={(e) => {
                            const arr = JSON.parse(JSON.stringify(cfg.bekannte_fehler));
                            const alt = arr[i];
                            const neu = { name: alt.name, aktiv: alt.aktiv !== false };
                            if (alt.genehmigt) { neu.genehmigt = true; neu.bemerkung = alt.bemerkung; neu.quelle = alt.quelle; }
                            if (e.target.value === "umbruch_artefakt") neu.regel = "umbruch_artefakt";
                            else if (e.target.value === "nur_kategorie") { neu.regel = "nur_kategorie"; neu.kategorie = alt.kategorie || "Whitespace"; }
                            else if (e.target.value === "regex") { neu.regex_alt = alt.regex_alt || alt.alt || ""; neu.regex_neu = alt.regex_neu || alt.neu || ""; }
                            else { neu.alt = alt.alt || ""; neu.neu = alt.neu || ""; }
                            arr[i] = neu;
                            setC("bekannte_fehler", arr);
                          }}>
                            <option value="woertlich">wörtlich (Alt→Neu)</option>
                            <option value="regex">Regex</option>
                            <option value="umbruch_artefakt">Umbruch-Artefakt</option>
                            <option value="nur_kategorie">ganze Kategorie</option>
                          </select>
                        </td>
                        {typ === "umbruch_artefakt" ? (
                          <td colSpan={2} className="vb-mini">Silbentrennung/Zeilenumbruch automatisch als bekannt</td>
                        ) : typ === "nur_kategorie" ? (
                          <td colSpan={2}>
                            <select className="vb-input" value={b.kategorie || "Whitespace"} onChange={(e) => bfSet(i, "kategorie", e.target.value)}>
                              {["Whitespace", "Interpunktion", "Zahlen/Betraege", "Text/Wort"].map(k => <option key={k}>{k}</option>)}
                            </select>
                          </td>
                        ) : typ === "regex" ? (
                          <>
                            <td><input className="vb-input" value={b.regex_alt || ""} onChange={(e) => bfSet(i, "regex_alt", e.target.value)} /></td>
                            <td><input className="vb-input" value={b.regex_neu || ""} onChange={(e) => bfSet(i, "regex_neu", e.target.value)} /></td>
                          </>
                        ) : (
                          <>
                            <td><input className="vb-input" value={b.alt || ""} onChange={(e) => bfSet(i, "alt", e.target.value)} /></td>
                            <td><input className="vb-input" value={b.neu || ""} onChange={(e) => bfSet(i, "neu", e.target.value)} /></td>
                          </>
                        )}
                        <td>
                          {(typ === "woertlich" || typ === "regex") ? (
                            <select className="vb-input" value={b.genehmigt ? "genehmigt" : "bekannt"}
                                    title={b.quelle ? `übernommen aus ${b.quelle}` : ""}
                                    onChange={(e) => bfSet(i, "genehmigt", e.target.value === "genehmigt")}>
                              <option value="bekannt">bekannt (grau)</option>
                              <option value="genehmigt">genehmigt (ausblenden)</option>
                            </select>
                          ) : <span className="vb-mini">bekannt</span>}
                        </td>
                        <td><button className="vb-x" onClick={() => setC("bekannte_fehler", cfg.bekannte_fehler.filter((_, j) => j !== i))}>✕</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button className="vb-btn small" onClick={bfAdd}>+ Bekannter Fehler</button>
            </details>

            <details className="vb-sect">
              <summary>Bündelung & Ausgabe</summary>
              <div className="vb-grid4">
                <label>„Systematisch" ab (Vorkommen)<input className="vb-input" type="number" min="2" value={cfg.buendelung.systematisch_ab} onChange={(e) => setC("buendelung.systematisch_ab", +e.target.value || 10)} /></label>
                <label>Beispiele je Cluster<input className="vb-input" type="number" min="1" value={cfg.buendelung.beispiele_pro_cluster} onChange={(e) => setC("buendelung.beispiele_pro_cluster", +e.target.value || 3)} /></label>
                <label className="vb-check-inline"><input type="checkbox" checked={cfg.buendelung.umbruch_artefakte_ausblenden} onChange={(e) => setC("buendelung.umbruch_artefakte_ausblenden", e.target.checked)} /> Umbruch-Artefakte ausblenden</label>
                <label>Report-Titel<input className="vb-input" value={cfg.ausgabe.titel} onChange={(e) => setC("ausgabe.titel", e.target.value)} /></label>
              </div>
              <div className="vb-checks">
                {[["excel", "Excel (Fehler-Cluster)"], ["html", "HTML (Gegenüberstellung)"],
                  ["pdf", "PDF-Bericht (Testdoku)"], ["csv", "CSV (Fehler-Cluster)"]].map(([f, label]) => (
                  <label key={f}><input type="checkbox" checked={cfg.ausgabe.formate.includes(f)}
                    onChange={(e) => setC("ausgabe.formate", e.target.checked ? [...cfg.ausgabe.formate, f] : cfg.ausgabe.formate.filter(x => x !== f))} /> {label}</label>
                ))}
              </div>
            </details>
          </div>

          {/* Start + Fortschritt + Ergebnis */}
          <div className="vb-card">
            <div className="vb-row-flex">
              <button className="vb-btn primary big" onClick={() => starten()}
                      disabled={startBusy || lauf?.status === "running" || beideAlt
                                || (!dateien.alt.length && !nMtextAlt && !dateien.extras.some(f => f.toLowerCase().endsWith(".html")))}>
                {(lauf?.status === "running" || startBusy) && !startModus ? "⏳ Vergleich läuft…" : "▶ Vergleich starten"}
              </button>
              <button className="vb-btn big" onClick={() => starten("bausteine_variablen")}
                      title="Bausteinlisten (NAME: / ##### / Inhalt / #####): jeder Baustein von Anfang bis Ende gegen den gleichnamigen Baustein — Seitenwechsel egal. Variablen werden mitgeprüft; MText-Eigenheiten (VBSA05 → VBSA0501, _1A/_2B) werden klassifiziert."
                      disabled={startBusy || lauf?.status === "running" || beideAlt || !(dateien.alt.length || nMtextAlt)}>
                {(lauf?.status === "running" || startBusy) && startModus ? "⏳ Bausteinvergleich läuft…" : "🧩 Bausteine & Variablen vergleichen"}
              </button>
              <span className="vb-mini">
                {nMtextAlt ? `${nMtextAlt} MText alt` : `${dateien.alt.length} Dopix`} · {dateien.neu.length} MText neu
                {dateien.extras.length ? ` · ${dateien.extras.length} Extras` : ""} · {vergleichsart}
              </span>
            </div>

            {lauf && (
              <div className="vb-progress-wrap">
                <div className="vb-row-flex">
                  {statusBadge(lauf.status)}
                  {lauf.total > 0 && <span className="vb-mini">{lauf.done}/{lauf.total} Paare</span>}
                </div>
                {lauf.status === "running" && (
                  <div className="vb-bar"><div className="vb-bar-fill" style={{ width: lauf.total ? `${Math.round(100 * lauf.done / lauf.total)}%` : "15%" }} /></div>
                )}
                {(lauf.log || []).length > 0 && (
                  <pre className="vb-log">{lauf.log.join("\n")}</pre>
                )}
              </div>
            )}

            {fertig && (
              <div className="vb-ergebnis">
                <div className="vb-kpis">
                  {/* Die Kacheln sind Sprungmarken in die Gegenüberstellung:
                      Der Bericht bringt die passenden Filter selbst mit, hier
                      wird nur der Anker mitgegeben (#nur-sauber / #nur-systematisch). */}
                  <div className="vb-kpi"><div className="vb-kpi-val">{stat.paare ?? "–"}</div><div className="vb-kpi-label">Dokumente</div></div>
                  <div className={`vb-kpi gruen${lauf.hat_html ? " vb-kpi-klick" : ""}`}
                       onClick={() => lauf.hat_html && window.open(`${API}/laeufe/${laufId}/report.html?${authQuery()}#nur-sauber`, "_blank")}
                       title={lauf.hat_html ? "Die fehlerfreien Dokumente in der Gegenüberstellung ansehen" : ""}>
                    <div className="vb-kpi-val">{stat.ok_dokumente ?? "–"}</div><div className="vb-kpi-label">✓ ohne echte Fehler</div></div>
                  <div className={`vb-kpi rot${lauf.hat_html ? " vb-kpi-klick" : ""}`}
                       onClick={() => lauf.hat_html && window.open(`${API}/laeufe/${laufId}/report.html?${authQuery()}#nur-systematisch`, "_blank")}
                       title={lauf.hat_html ? "Die systematischen Fehler in der Gegenüberstellung ansehen" : ""}>
                    <div className="vb-kpi-val">{stat.systematisch ?? "–"}</div><div className="vb-kpi-label">Systematisch</div></div>
                  <div className={`vb-kpi gelb${lauf.hat_html ? " vb-kpi-klick" : ""}`}
                       onClick={() => lauf.hat_html && window.open(`${API}/laeufe/${laufId}/report.html?${authQuery()}#nur-einzelfaelle`, "_blank")}
                       title={lauf.hat_html ? "Die Einzelfälle in der Gegenüberstellung ansehen" : ""}>
                    <div className="vb-kpi-val">{(stat.echte ?? 0) - (stat.systematisch ?? 0)}</div><div className="vb-kpi-label">Einzelfälle</div></div>
                  <div className="vb-kpi grau"><div className="vb-kpi-val">{stat.bekannt == null ? "–" : stat.bekannt - (stat.klassifiziert || 0)}</div><div className="vb-kpi-label">Bekannt</div></div>
                  {(stat.klassifiziert || 0) > 0 && (
                    <div className="vb-kpi grau" title="MText-Eigenheiten (Variablen-Index 01/02, Baustein-Varianten _1A/_2B …) — benannt, aber nicht als Einzelfehler gezählt">
                      <div className="vb-kpi-val">{stat.klassifiziert}</div><div className="vb-kpi-label">klassifiziert</div></div>
                  )}
                  {(stat.genehmigt || 0) > 0 && (
                    <div className="vb-kpi gruen" title="In der Excel oder im Profil auf „genehmigt“ gesetzt — in diesem Lauf ausgeblendet">
                      <div className="vb-kpi-val">{stat.genehmigt}</div><div className="vb-kpi-label">genehmigt (ausgeblendet)</div></div>
                  )}
                </div>
                {genehmigtAusExcel.length > 0 && (
                  <div className="vb-uebernahme">
                    <div>
                      <b>✔ {genehmigtAusExcel.length} Fehlerbild{genehmigtAusExcel.length === 1 ? "" : "er"} aus der Excel genehmigt.</b>{" "}
                      Ins Profil übernehmen — dann blenden alle weiteren Läufe mit diesem Profil sie aus,
                      ohne dass die Excel noch einmal hochgeladen werden muss.
                    </div>
                    {!insProfil?.ok && (
                      <button className="vb-btn primary" disabled={insProfil?.busy} onClick={insProfilUebernehmen}>
                        {insProfil?.busy ? "⏳ übernimmt…"
                          : selProfil ? `➕ Ins Profil „${profilName}“ übernehmen` : "💾 Als Profil speichern und übernehmen"}
                      </button>
                    )}
                    {insProfil?.ok && (
                      <div className="vb-uebernahme-ok">
                        ✓ Im Profil „{insProfil.profil}“: {insProfil.neu} neu aufgenommen
                        {insProfil.aktualisiert ? `, ${insProfil.aktualisiert} auf „genehmigt“ umgestellt` : ""}
                        {insProfil.unveraendert ? `, ${insProfil.unveraendert} waren schon drin` : ""}.
                        Beim nächsten Lauf mit diesem Profil ist die Excel nicht mehr nötig.
                      </div>
                    )}
                    {insProfil?.fehler && <div className="vb-error">{insProfil.fehler}</div>}
                  </div>
                )}
                <div className="vb-mini" style={{ marginTop: -4, marginBottom: 8 }}>Vergleich: {vergleichsart}
                  {stat.vergleichsart === "bausteine_variablen" && (
                    <> · 🧩 Baustein- und Variablenvergleich
                      {stat.bausteine ? ` (${stat.bausteine.gleich} von ${stat.bausteine.gesamt} Bausteinen gleich)` : ""}</>
                  )}</div>
                {(stat.top || []).length > 0 && (
                  <details className="vb-sect" open>
                    <summary>Top-Fehler (jeder einmal, mit Zähler)</summary>
                    <table className="vb-table">
                      <thead><tr><th>Häufigkeit</th><th>Kategorie</th><th>Alt</th><th>Neu</th></tr></thead>
                      <tbody>
                        {stat.top.slice(0, 10).map((t, i) => (
                          <tr key={i}><td>{t[0]}×</td><td>{t[1]}</td><td className="vb-diff-alt">{t[2]}</td><td className="vb-diff-neu">{t[3]}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}

                {(stat.dokumente || []).length > 0 && (
                  <details className="vb-sect" open>
                    <summary>Dokumente ({stat.dokumente.length})</summary>
                    <div className="vb-row-flex" style={{ margin: "8px 0" }}>
                      <input className="vb-input" style={{ maxWidth: 240 }} placeholder="VNR filtern…"
                             value={docFilter} onChange={(e) => setDocFilter(e.target.value)} />
                      <label className="vb-check-inline" style={{ fontSize: 13 }}>
                        <input type="checkbox" checked={docNurFehler} onChange={(e) => setDocNurFehler(e.target.checked)} />
                        nur Dokumente mit echten Fehlern
                      </label>
                      <span className="vb-mini">{dokumente.length} angezeigt</span>
                    </div>
                    <div className="vb-doc-scroll">
                      <table className="vb-table">
                        <thead><tr><th>VNR</th><th>Status</th><th>identisch</th><th>geändert</th><th>nur ALT</th><th>nur NEU</th><th>bekannt</th><th></th></tr></thead>
                        <tbody>
                          {dokumente.slice(0, 300).map(d => (
                            <tr key={d.vnr}>
                              <td><strong>{d.vnr}</strong></td>
                              <td>{(d.echte || 0) === 0
                                ? <span className="vb-chip ok">✓ OK</span>
                                : <span className="vb-chip warn">{d.echte} echte</span>}</td>
                              <td className="vb-num ident">{d.identisch ?? "–"}</td>
                              <td className="vb-num">{d.geaendert}</td>
                              <td className="vb-num">{d.nur_alt}</td>
                              <td className="vb-num">{d.nur_neu}</td>
                              <td className="vb-num grau">{d.bekannt}</td>
                              <td>
                                {lauf.hat_html && (
                                  <button className="vb-btn small" title="Gegenüberstellung dieser VNR öffnen"
                                          onClick={() => window.open(`${API}/laeufe/${laufId}/report.html?${authQuery()}#v${d.vnr}`, "_blank")}>Details</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {dokumente.length > 300 && <div className="vb-mini">… und {dokumente.length - 300} weitere — Filter nutzen.</div>}
                    </div>
                  </details>
                )}

                <div className="vb-row-flex" style={{ marginTop: 12 }}>
                  {lauf.hat_html && <button className="vb-btn primary" onClick={() => window.open(`${API}/laeufe/${laufId}/report.html?${authQuery()}`, "_blank")}>🌐 Gegenüberstellung öffnen</button>}
                  {lauf.hat_xlsx && <button className="vb-btn" onClick={() => window.open(`${API}/laeufe/${laufId}/report.xlsx?${authQuery()}`, "_blank")}>⬇ Excel</button>}
                  {lauf.hat_pdf && <button className="vb-btn" onClick={() => window.open(`${API}/laeufe/${laufId}/report.pdf?${authQuery()}`, "_blank")}>⬇ PDF-Bericht</button>}
                  {lauf.hat_csv && <button className="vb-btn" onClick={() => window.open(`${API}/laeufe/${laufId}/report.csv?${authQuery()}`, "_blank")}>⬇ CSV</button>}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ════════ Tab: Läufe (Historie) ════════ */}
      {tab === "laeufe" && (
        <div className="vb-card">
          <div className="vb-card-title">Bisherige Läufe
            <button className="vb-btn small ghost" style={{ marginLeft: "auto" }} onClick={loadLaeufe}>↻ Aktualisieren</button>
          </div>
          {laeufe.length === 0 ? <div className="vb-mini">Noch keine Läufe.</div> : (
            <table className="vb-table">
              <thead><tr><th>Name</th><th>Datum</th><th>Status</th><th>Dateien</th><th>Dokumente</th><th>Echte Fehler</th><th>Systematisch</th><th>Aktionen</th></tr></thead>
              <tbody>
                {laeufe.map(l => (
                  <tr key={l.id}>
                    <td>{l.name || <span className="vb-mini">({l.id.slice(0, 8)})</span>}</td>
                    <td>{(l.created_at || "").slice(0, 16)}</td>
                    <td>{statusBadge(l.status)}{l.status === "running" && l.total > 0 && <span className="vb-mini"> {l.done}/{l.total}</span>}</td>
                    <td>{l.n_alt}+{l.n_neu}</td>
                    <td>{l.paare ?? "–"}</td>
                    <td>{l.echte ?? "–"}</td>
                    <td>{l.systematisch ?? "–"}</td>
                    <td className="vb-aktionen">
                      <button className="vb-btn small" title="Gegenüberstellung" onClick={() => window.open(`${API}/laeufe/${l.id}/report.html?${authQuery()}`, "_blank")}>🌐</button>
                      <button className="vb-btn small" title="Excel" onClick={() => window.open(`${API}/laeufe/${l.id}/report.xlsx?${authQuery()}`, "_blank")}>⬇</button>
                      <button className="vb-btn small" title="Einstellungen als neuen Lauf übernehmen" onClick={() => configUebernehmen(l.id)}>↩︎</button>
                      <button className="vb-btn small ghost" title="Löschen" onClick={() => laufLoeschen(l.id)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ════════ Tab: Profile ════════ */}
      {tab === "profile" && (
        <div className="vb-card">
          <div className="vb-card-title">Vergleichs-Profile</div>
          <table className="vb-table">
            <thead><tr><th>Name</th><th>Regionen</th><th>Bekannte Fehler</th><th>Systematisch ab</th><th>Geändert</th><th>Aktionen</th></tr></thead>
            <tbody>
              {profile.map(p => (
                <tr key={p.id}>
                  <td><strong>{p.name}</strong></td>
                  <td>{(p.config?.regionen_ignorieren || []).length}</td>
                  <td>{(p.config?.bekannte_fehler || []).length}</td>
                  <td>{p.config?.buendelung?.systematisch_ab ?? "–"}</td>
                  <td>{(p.updated_at || "").slice(0, 16)}</td>
                  <td className="vb-aktionen">
                    <button className="vb-btn small" title="Im neuen Lauf verwenden" onClick={() => { profilAnwenden(String(p.id)); setTab("neu"); }}>▶</button>
                    <button className="vb-btn small" title="Details" onClick={() => setProfilDetail(profilDetail?.id === p.id ? null : p)}>👁</button>
                    <button className="vb-btn small" title="Umbenennen" onClick={() => profilUmbenennen(p)}>✎</button>
                    <button className="vb-btn small ghost" title="Löschen" onClick={() => profilLoeschen(p.id)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {profilDetail && (
            <pre className="vb-log" style={{ maxHeight: 400 }}>{JSON.stringify(profilDetail.config, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}
